import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import './CSS/DemoLeads.css';

const STAGES = [
  'new',
  'assigned',
  'contacted',
  'demo_scheduled',
  'demo_completed',
  'followup',
  'converted',
];

const STATUS_OPTIONS = [
  'new',
  'assigned',
  'contacted',
  'demo_scheduled',
  'demo_completed',
  'followup',
  'converted',
  'lost',
];

const initialActionForm = {
  action_type: 'note',
  action_note: '',
};

const initialConvertForm = {
  admin_name: '',
  admin_phone: '',
  admin_password: '',
  tenant_code: '',
};

export default function DemoLeads() {
  const token = localStorage.getItem('token');

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyLeadId, setBusyLeadId] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [statusModalLead, setStatusModalLead] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('new');

  const [assignModalLead, setAssignModalLead] = useState(null);
  const [assignedTo, setAssignedTo] = useState('');

  const [actionModalLead, setActionModalLead] = useState(null);
  const [actionForm, setActionForm] = useState(initialActionForm);

  const [convertModalLead, setConvertModalLead] = useState(null);
  const [convertForm, setConvertForm] = useState(initialConvertForm);

  const authHeaders = {
    headers: { Authorization: `Bearer ${token}` },
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/demo-requests/platform', authHeaders);
      setLeads(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to load demo leads');
    } finally {
      setLoading(false);
    }
  };

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();

    return leads.filter((lead) => {
      const matchesStatus = statusFilter === 'all' ? true : lead.status === statusFilter;
      const blob = [
        lead.company_name,
        lead.contact_person,
        lead.phone,
        lead.email,
        lead.city,
        lead.state,
        lead.assigned_to_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = q ? blob.includes(q) : true;
      return matchesStatus && matchesSearch;
    });
  }, [leads, search, statusFilter]);

  const summary = useMemo(() => {
    return {
      total: leads.length,
      fresh: leads.filter((l) => l.status === 'new').length,
      scheduled: leads.filter((l) => l.status === 'demo_scheduled').length,
      followup: leads.filter((l) => l.status === 'followup').length,
      converted: leads.filter((l) => l.status === 'converted').length,
      lost: leads.filter((l) => l.status === 'lost').length,
    };
  }, [leads]);

  const quickStatusUpdate = async (lead, nextStatus) => {
    try {
      setBusyLeadId(lead.id);
      await api.put(
        `/api/demo-requests/platform/${lead.id}/status`,
        { status: nextStatus },
        authHeaders
      );
      await fetchLeads();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update lead status');
    } finally {
      setBusyLeadId(null);
    }
  };

  const openStatusModal = (lead) => {
    setStatusModalLead(lead);
    setSelectedStatus(lead.status || 'new');
  };

  const openAssignModal = (lead) => {
    setAssignModalLead(lead);
    setAssignedTo(lead.assigned_to ? String(lead.assigned_to) : '');
  };

  const openActionModal = (lead) => {
    setActionModalLead(lead);
    setActionForm(initialActionForm);
  };

  const openConvertModal = (lead) => {
    setConvertModalLead(lead);
    setConvertForm({
      admin_name: lead.contact_person || '',
      admin_phone: lead.phone || '',
      admin_password: '',
      tenant_code: '',
    });
  };

  const updateLeadStatus = async () => {
    if (!statusModalLead) return;

    try {
      setBusyLeadId(statusModalLead.id);
      await api.put(
        `/api/demo-requests/platform/${statusModalLead.id}/status`,
        { status: selectedStatus },
        authHeaders
      );
      setStatusModalLead(null);
      await fetchLeads();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update lead status');
    } finally {
      setBusyLeadId(null);
    }
  };

  const assignLead = async () => {
    if (!assignModalLead) return;
    if (!assignedTo) {
      alert('Enter assigned user id');
      return;
    }

    try {
      setBusyLeadId(assignModalLead.id);
      await api.put(
        `/api/demo-requests/platform/${assignModalLead.id}/assign`,
        { assigned_to: Number(assignedTo) },
        authHeaders
      );
      setAssignModalLead(null);
      setAssignedTo('');
      await fetchLeads();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to assign lead');
    } finally {
      setBusyLeadId(null);
    }
  };

  const addAction = async (e) => {
    e.preventDefault();
    if (!actionModalLead) return;

    try {
      setBusyLeadId(actionModalLead.id);
      await api.post(
        `/api/demo-requests/platform/${actionModalLead.id}/action`,
        actionForm,
        authHeaders
      );
      setActionModalLead(null);
      setActionForm(initialActionForm);
      await fetchLeads();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to add lead action');
    } finally {
      setBusyLeadId(null);
    }
  };

  const convertLead = async (e) => {
    e.preventDefault();
    if (!convertModalLead) return;

    try {
      setBusyLeadId(convertModalLead.id);
      await api.post(
        `/api/demo-requests/platform/${convertModalLead.id}/convert`,
        convertForm,
        authHeaders
      );
      setConvertModalLead(null);
      setConvertForm(initialConvertForm);
      await fetchLeads();
      alert('Lead converted successfully');
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to convert lead');
    } finally {
      setBusyLeadId(null);
    }
  };

  return (
    <div className="demo-leads-page">
      <section className="lead-hero">
        <div>
          <div className="lead-badge">Sales Flow • Demo Request Engine</div>
          <h1>Demo Lead Pipeline</h1>
          <p>
            Every request for demo becomes a structured lead with lifecycle tracking,
            stage progression, conversion handling, and smooth operator actions.
          </p>
        </div>

        <div className="lead-hero-actions">
          <button className="lead-primary-btn" onClick={fetchLeads}>
            Refresh Pipeline
          </button>
        </div>
      </section>

      <section className="lead-summary-grid">
        <LeadMetric title="Total Leads" value={summary.total} hint="All requests for demo" />
        <LeadMetric title="Fresh Leads" value={summary.fresh} hint="Need first response" glow="cyan" />
        <LeadMetric title="Scheduled" value={summary.scheduled} hint="Demo arranged" glow="purple" />
        <LeadMetric title="Follow-up" value={summary.followup} hint="Decision pending" glow="orange" />
        <LeadMetric title="Converted" value={summary.converted} hint="Now paying tenants" glow="green" />
        <LeadMetric title="Lost" value={summary.lost} hint="Not progressed" glow="red" />
      </section>

      <section className="lead-control-panel">
        <div className="lead-panel-head">
          <div>
            <div className="lead-section-tag">Pipeline control</div>
            <h3>Manage demo requests</h3>
          </div>

          <div className="lead-toolbar">
            <input
              className="lead-search"
              placeholder="Search company, contact, phone, email, city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              className="lead-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {formatStatus(status)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="lead-empty">Loading demo leads...</div>
        ) : filteredLeads.length === 0 ? (
          <div className="lead-empty">No demo leads matched the current filters.</div>
        ) : (
          <div className="lead-table-shell">
            <table className="lead-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Lead</th>
                  <th>Stage Timeline</th>
                  <th>Status</th>
                  <th>Assigned</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.id}>
                    <td className="lead-id-cell">#{lead.id}</td>

                    <td className="lead-main-cell">
                      <div className="lead-company">{lead.company_name}</div>
                      <div className="lead-subline">{lead.contact_person} • {lead.phone}</div>
                      <div className="lead-subline muted">
                        {lead.email || '-'}
                        {(lead.city || lead.state) ? ` • ${lead.city || ''}${lead.city && lead.state ? ', ' : ''}${lead.state || ''}` : ''}
                      </div>
                    </td>

                    <td className="lead-timeline-cell">
                      <LeadTimeline status={lead.status} />
                    </td>

                    <td>
                      <span className={`lead-status-chip ${lead.status || 'new'}`}>
                        {formatStatus(lead.status)}
                      </span>
                    </td>

                    <td>
                      <div className="assigned-block">
                        <span>{lead.assigned_to_name || '-'}</span>
                      </div>
                    </td>

                    <td>{formatDateTime(lead.created_at)}</td>

                    <td>
                      <div className="lead-actions-grid">
                        <button
                          className="lead-mini-btn"
                          onClick={() => openStatusModal(lead)}
                          disabled={busyLeadId === lead.id}
                        >
                          Change Status
                        </button>

                        <button
                          className="lead-mini-btn"
                          onClick={() => openAssignModal(lead)}
                          disabled={busyLeadId === lead.id}
                        >
                          Assign
                        </button>

                        <button
                          className="lead-mini-btn"
                          onClick={() => openActionModal(lead)}
                          disabled={busyLeadId === lead.id}
                        >
                          Add Note
                        </button>

                        {lead.status !== 'contacted' && lead.status !== 'converted' && lead.status !== 'lost' && (
                          <button
                            className="lead-mini-btn ghost-accent"
                            onClick={() => quickStatusUpdate(lead, 'contacted')}
                            disabled={busyLeadId === lead.id}
                          >
                            Mark Contacted
                          </button>
                        )}

                        {lead.status !== 'demo_scheduled' && lead.status !== 'converted' && lead.status !== 'lost' && (
                          <button
                            className="lead-mini-btn ghost-accent"
                            onClick={() => quickStatusUpdate(lead, 'demo_scheduled')}
                            disabled={busyLeadId === lead.id}
                          >
                            Schedule Demo
                          </button>
                        )}

                        {lead.status !== 'followup' && lead.status !== 'converted' && lead.status !== 'lost' && (
                          <button
                            className="lead-mini-btn ghost-accent"
                            onClick={() => quickStatusUpdate(lead, 'followup')}
                            disabled={busyLeadId === lead.id}
                          >
                            Follow-up
                          </button>
                        )}

                        {lead.status !== 'converted' && (
                          <button
                            className="lead-mini-btn primary"
                            onClick={() => openConvertModal(lead)}
                            disabled={busyLeadId === lead.id}
                          >
                            Convert
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {statusModalLead && (
        <ModalShell title={`Update Status • ${statusModalLead.company_name}`} onClose={() => setStatusModalLead(null)}>
          <div className="modal-stack">
            <label className="modal-label">Select new status</label>
            <select
              className="modal-input"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {formatStatus(status)}
                </option>
              ))}
            </select>

            <div className="modal-action-row">
              <button className="modal-btn ghost" onClick={() => setStatusModalLead(null)}>
                Cancel
              </button>
              <button className="modal-btn primary" onClick={updateLeadStatus}>
                Save Status
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {assignModalLead && (
        <ModalShell title={`Assign Lead • ${assignModalLead.company_name}`} onClose={() => setAssignModalLead(null)}>
          <div className="modal-stack">
            <label className="modal-label">Assigned User ID</label>
            <input
              className="modal-input"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              placeholder="Enter support or platform user id"
            />
            <div className="modal-help">
              Use the platform/support user id that should handle this lead.
            </div>

            <div className="modal-action-row">
              <button className="modal-btn ghost" onClick={() => setAssignModalLead(null)}>
                Cancel
              </button>
              <button className="modal-btn primary" onClick={assignLead}>
                Assign Lead
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {actionModalLead && (
        <ModalShell title={`Add Action • ${actionModalLead.company_name}`} onClose={() => setActionModalLead(null)}>
          <form className="modal-stack" onSubmit={addAction}>
            <label className="modal-label">Action Type</label>
            <select
              className="modal-input"
              value={actionForm.action_type}
              onChange={(e) => setActionForm({ ...actionForm, action_type: e.target.value })}
            >
              <option value="note">note</option>
              <option value="called">called</option>
              <option value="whatsapp_sent">whatsapp_sent</option>
              <option value="followup_added">followup_added</option>
              <option value="demo_discussed">demo_discussed</option>
            </select>

            <label className="modal-label">Action Note</label>
            <textarea
              className="modal-input modal-textarea"
              value={actionForm.action_note}
              onChange={(e) => setActionForm({ ...actionForm, action_note: e.target.value })}
              placeholder="Write what happened with this lead..."
              required
            />

            <div className="modal-action-row">
              <button type="button" className="modal-btn ghost" onClick={() => setActionModalLead(null)}>
                Cancel
              </button>
              <button type="submit" className="modal-btn primary">
                Save Action
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {convertModalLead && (
        <ModalShell title={`Convert to Tenant • ${convertModalLead.company_name}`} onClose={() => setConvertModalLead(null)}>
          <form className="modal-stack" onSubmit={convertLead}>
            <label className="modal-label">Admin Name</label>
            <input
              className="modal-input"
              value={convertForm.admin_name}
              onChange={(e) => setConvertForm({ ...convertForm, admin_name: e.target.value })}
              required
            />

            <label className="modal-label">Admin Phone</label>
            <input
              className="modal-input"
              value={convertForm.admin_phone}
              onChange={(e) => setConvertForm({ ...convertForm, admin_phone: e.target.value })}
              required
            />

            <label className="modal-label">Admin Password</label>
            <input
              type="password"
              className="modal-input"
              value={convertForm.admin_password}
              onChange={(e) => setConvertForm({ ...convertForm, admin_password: e.target.value })}
              required
            />

            <label className="modal-label">Tenant Code</label>
            <input
              className="modal-input"
              value={convertForm.tenant_code}
              onChange={(e) => setConvertForm({ ...convertForm, tenant_code: e.target.value.toUpperCase() })}
              placeholder="Ex: ABC001"
              required
            />

            <div className="modal-action-row">
              <button type="button" className="modal-btn ghost" onClick={() => setConvertModalLead(null)}>
                Cancel
              </button>
              <button type="submit" className="modal-btn primary">
                Convert Lead
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
}

function LeadMetric({ title, value, hint, glow = 'blue' }) {
  return (
    <div className={`lead-metric-card glow-${glow}`}>
      <div className="lead-metric-title">{title}</div>
      <div className="lead-metric-value">{value}</div>
      <div className="lead-metric-hint">{hint}</div>
    </div>
  );
}

function LeadTimeline({ status }) {
  const currentIndex =
    status === 'lost'
      ? -1
      : Math.max(STAGES.findIndex((s) => s === (status || 'new')), 0);

  return (
    <div className="timeline-track">
      {STAGES.map((stage, index) => {
        const completed = currentIndex > index;
        const active = currentIndex === index;
        const upcoming = currentIndex < index;
        return (
          <div
            className={`timeline-node ${completed ? 'completed' : ''} ${active ? 'active' : ''} ${upcoming ? 'upcoming' : ''}`}
            key={stage}
            title={formatStatus(stage)}
          >
            <span className="timeline-dot" />
            <span className="timeline-label">{formatStatus(stage)}</span>
            {index < STAGES.length - 1 && <span className="timeline-line" />}
          </div>
        );
      })}
      {status === 'lost' && <div className="timeline-lost-tag">Lost</div>}
    </div>
  );
}

function ModalShell({ title, children, onClose }) {
  return (
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="platform-modal-head">
          <h3>{title}</h3>
          <button className="platform-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function formatStatus(status) {
  return String(status || 'new')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}